import { useState } from 'react'
import { AnimatePresence, m } from 'framer-motion'
import clsx from 'clsx'
import { isDesktop } from 'react-device-detect'
import { useAtom } from 'jotai'
import { isShowLoadingAtom, isShowVimeoAtom, isFirstVisitAtom } from '@/stores'
import { useIsClient } from '@/hooks'
import { VimeoMedia, IconPlayVideo } from '@/components'
import { getExtension } from '@/libs/utils'
import type { SettingsType } from '@/@types/microcms-type'

type LoadingProps = {
    settings: SettingsType
}

export const Loading = ({ settings }: LoadingProps) => {
    const isClient = useIsClient()
    const [isShowLoading] = useAtom(isShowLoadingAtom)
    const [showVimeo, setShowVimeo] = useAtom(isShowVimeoAtom)
    const [isFirst] = useAtom(isFirstVisitAtom)
    const { showreel, vimeo } = settings

    const mediaProps = {
        src: showreel.video.url,
        type: `video/${getExtension(showreel.video.url)}`,
        width: showreel.poster.width ?? 1,
        height: showreel.poster.height ?? 1,
        alt: '',
        sizes: '100vw',
        quality: 75,
        poster: showreel.poster.url + '?w=640&q=10&fm=webp',
        controls: false,
        muted: true,
        autoPlay: true,
        playsInline: true,
        loop: true
    }

    const [isMouseMoving, setIsMouseMoving] = useState<boolean>(false)

    const handleMouseMove = () => {
        setIsMouseMoving(true)
        setTimeout(() => {
            setIsMouseMoving(false)
        }, 2500)
    }

    return (
        <AnimatePresence mode="sync">
            {!isShowLoading && (
                <m.div
                    initial={{
                        opacity: 1,
                        backgroundColor: '#ffffff'
                    }}
                    animate={{
                        opacity: 1,
                        backgroundColor: '#000',
                        transition: { duration: 0.45 }
                    }}
                    exit={{
                        opacity: 0,
                        backgroundColor: '#fff',
                        transition: { duration: 1.2 }
                    }}
                    onMouseMove={handleMouseMove}
                    className="fixed z-overlay w-screen h-d-screen grid items-center justify-center overflow-hidden"
                >
                    <div className="w-screen h-d-screen relative">
                        <VimeoMedia
                            vimeo={vimeo}
                            showVimeo={showVimeo}
                            setShowVimeo={setShowVimeo}
                        />

                        {isClient && (
                            <m.div
                                initial={{
                                    opacity: 0
                                }}
                                animate={{
                                    opacity: 1,
                                    transition: { duration: 0.6, delay: 0.6 }
                                }}
                                className={clsx(
                                    'w-screen h-d-screen relative z-0',
                                    "after:content-[''] after:block after:w-screen after:h-d-screen after:absolute after:top-0 after:left-0 after:z-1 after:duration-[2000ms]",
                                    !isDesktop || isMouseMoving
                                        ? 'after:bg-v-black/70'
                                        : 'after:bg-v-black/10'
                                )}
                            >
                                <m.video
                                    initial={{
                                        opacity: 0
                                    }}
                                    animate={{
                                        opacity: 1,
                                        transition: { duration: 0.2 }
                                    }}
                                    exit={{
                                        opacity: 0.8,
                                        transition: { duration: 0.4 }
                                    }}
                                    {...mediaProps}
                                    className="w-screen h-d-screen relative object-cover object-center"
                                />
                            </m.div>
                        )}

                        <m.button
                            initial={{
                                opacity: 0
                            }}
                            animate={{
                                opacity: 1,
                                transition: { duration: 0.6, delay: 1.2 }
                            }}
                            onClick={() => setShowVimeo(true)}
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 md:hover:!opacity-70 duration-[200ms] md:hover:scale-[0.95]"
                        >
                            <IconPlayVideo className="w-[16vw] h-[16vw] md:w-[5vw] md:h-[5vw]" />
                        </m.button>
                    </div>
                </m.div>
            )}
        </AnimatePresence>
    )
}
